import { useRef, useState } from 'react';
import { Popconfirm, Spin, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import callApi, { uploadImageApiCall } from '../../Api/config';
import {
  DOCUMENT_DELETE_SINGLE,
  DOCUMITRA_ORDER_PROCESSED_PDF,
  DOCUMITRA_TEAM_DOCUMENT_UPLOAD,
} from '../../constants/EntityName';
import {
  FINAL_FORM_RETENTION_DAYS,
  PROCESSED_DOCUMENT_TYPES,
  ProcessedDocumentType,
  getMemberProcessedDocuments,
} from '../../constants/ProcessedDocumentTypes';
import { BASE_IMAGE_URL } from '../../env';
import { toastError, toastSuccess } from '../../service/Toast';
import PdfViewer from '../PdfViewer';

const { Text } = Typography;

type TeamDocumentType = Exclude<ProcessedDocumentType, 'PREVIEW_FORM'>;

interface DocumitraTeamDocumentsProps {
  /** Database id of the order (formDetails.id) */
  orderId?: string;
  member: any;
  documentType: TeamDocumentType;
  /** Receives the member id and the full updated processedDocuments array */
  onDocumentsChange: (memberId: string, processedDocuments: any[]) => void;
}

const SECTION_META: Record<
  TeamDocumentType,
  { title: string; hint: string; accept: string; emptyText: string }
> = {
  [PROCESSED_DOCUMENT_TYPES.FINAL_FORM]: {
    title: 'Final Forms',
    hint: `Visible to the applicant as download-only. Each file is automatically deleted ${FINAL_FORM_RETENTION_DAYS} days after upload. No file size limit. The applicant is emailed only on the first upload.`,
    accept: 'application/pdf',
    emptyText: 'No final forms uploaded yet.',
  },
  [PROCESSED_DOCUMENT_TYPES.GOVERNMENT_DOCUMENT]: {
    title: 'Government Issued Documents',
    hint: 'Visible to the applicant as download-only and kept permanently. No file size limit.',
    accept: 'application/pdf,image/*',
    emptyText: 'No government issued documents uploaded yet.',
  },
};

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatDate = (value?: string | Date | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysUntil = (value?: string | Date | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
};

const withTimestamp = (fileName: string) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) return `${fileName}_${timestamp}`;
  return `${fileName.slice(0, dotIndex)}_${timestamp}${fileName.slice(dotIndex)}`;
};

/**
 * "Forms by Documitra" section for team-only uploads (Final Forms / Government Issued Documents).
 * The applicant can only view/download these on the web portal; accept/reject/remark is disabled.
 */
const DocumitraTeamDocuments = ({
  orderId,
  member,
  documentType,
  onDocumentsChange,
}: DocumitraTeamDocumentsProps) => {
  const meta = SECTION_META[documentType];
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});

  const allDocuments = getMemberProcessedDocuments(member);
  const documents = allDocuments.filter((doc: any) => doc?.documentType === documentType);
  const isFinalForm = documentType === PROCESSED_DOCUMENT_TYPES.FINAL_FORM;

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!orderId || !member?.id) {
      toastError('Order or member details missing — cannot upload');
      return;
    }

    try {
      setUploading(true);
      const uniqueFile = new File([file], withTimestamp(file.name), {
        type: file.type,
        lastModified: file.lastModified,
      });
      const formData = new FormData();
      formData.append('file', uniqueFile);
      formData.append('documentType', documentType);

      const uploadRes: any = await uploadImageApiCall(DOCUMITRA_TEAM_DOCUMENT_UPLOAD, formData);
      if (!uploadRes?.isSuccess || !uploadRes?.data?.url) {
        toastError(uploadRes?.message || 'File upload failed');
        return;
      }

      const saveRes = await callApi(`${DOCUMITRA_ORDER_PROCESSED_PDF}/${orderId}`, 'PUT', {
        memberId: member.id,
        name: uploadRes.data.name || uniqueFile.name,
        size: uploadRes.data.size || file.size,
        url: uploadRes.data.url,
        documentType,
      });

      if (saveRes?.status) {
        // Drop the nested member relation before storing the row in component state
        const { member: _member, ...savedDocument } = saveRes?.data?.data || {};
        const nextDocuments = [
          ...allDocuments.filter((doc: any) => String(doc?.id) !== String(savedDocument?.id)),
          { ...savedDocument, documentType },
        ];
        onDocumentsChange(member.id, nextDocuments);
        toastSuccess(
          isFinalForm
            ? 'Final form uploaded. The applicant has been notified by email.'
            : 'Government issued document uploaded.'
        );
      } else {
        toastError(saveRes?.data?.message || 'Failed to save the uploaded document');
      }
    } catch (error) {
      console.error('Team document upload error:', error);
      toastError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc: any) => {
    try {
      setDeletingIds(prev => ({ ...prev, [doc.id]: true }));
      const res = await callApi(`${DOCUMENT_DELETE_SINGLE}/${doc.id}`, 'DELETE');
      if (res?.status) {
        onDocumentsChange(
          member.id,
          allDocuments.filter((item: any) => String(item?.id) !== String(doc.id))
        );
        toastSuccess('Document deleted');
      } else {
        toastError(res?.data?.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Team document delete error:', error);
      toastError('Failed to delete document');
    } finally {
      setDeletingIds(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleDownload = (doc: any) => {
    const href =
      doc?.url?.startsWith('https') || !doc?.url ? doc?.url : `${BASE_IMAGE_URL}/${doc?.url}`;
    if (!href) return;
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', doc?.name || 'document');
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      style={{
        width: '100%',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        background: '#fff',
        padding: '18px 20px',
        marginBottom: '18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '6px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '16px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'black',
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {meta.title}
          <span
            style={{
              padding: '1px 9px',
              borderRadius: '999px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              color: '#475569',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.02em',
            }}
          >
            {documents.length} file{documents.length === 1 ? '' : 's'}
          </span>
          <span
            style={{
              padding: '1px 9px',
              borderRadius: '999px',
              border: isFinalForm ? '1px solid #fed7aa' : '1px solid #a7f3d0',
              background: isFinalForm ? '#fff7ed' : '#ecfdf5',
              color: isFinalForm ? '#c2410c' : '#047857',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              textTransform: 'none',
            }}
          >
            {isFinalForm ? `Auto-deletes after ${FINAL_FORM_RETENTION_DAYS} days` : 'Kept forever'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{
            backgroundColor: '#004195',
            borderRadius: '6px',
            color: 'white',
            fontWeight: 600,
            border: 'none',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: uploading ? 0.7 : 1,
          }}
        >
          {uploading ? <Spin size="small" style={{ color: 'white' }} /> : '+'}
          {uploading ? 'Uploading...' : `Upload ${isFinalForm ? 'Final Form' : 'Document'}`}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={meta.accept}
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
      </div>
      <Text type="secondary" style={{ display: 'block', marginBottom: '14px', fontSize: '13px' }}>
        {meta.hint} The applicant cannot accept, reject or add remarks on these files.
      </Text>

      {documents.length === 0 ? (
        <div
          style={{
            padding: '18px',
            borderRadius: '8px',
            border: '1px dashed #cbd5e1',
            backgroundColor: '#fafafa',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '13px',
          }}
        >
          {meta.emptyText}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
          }}
        >
          {documents.map((doc: any) => {
            const remainingDays = isFinalForm ? daysUntil(doc?.expiresAt) : null;
            return (
              <div
                key={doc.id}
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  padding: '10px',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '280px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '200px',
                    marginBottom: '10px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PdfViewer photo={doc?.url} fileName={doc?.name} isSmallPreview={true} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '4px',
                      gap: '8px',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '14px',
                        color: '#334155',
                        wordBreak: 'break-word',
                        flex: 1,
                        lineHeight: '1.25',
                        fontWeight: 600,
                      }}
                    >
                      {doc?.name || 'Untitled'}
                    </Text>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <Popconfirm
                        title="Delete this document?"
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(doc)}
                        disabled={!!deletingIds[doc.id]}
                      >
                        <DeleteOutlined
                          title="Delete"
                          style={{
                            backgroundColor: '#00000070',
                            padding: '4px',
                            borderRadius: '4px',
                            fontSize: 16,
                            color: 'white',
                            cursor: deletingIds[doc.id] ? 'not-allowed' : 'pointer',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        />
                      </Popconfirm>
                      <DownloadOutlined
                        title="Download"
                        style={{
                          backgroundColor: '#00000070',
                          borderRadius: '4px',
                          padding: '4px',
                          fontSize: 16,
                          color: 'white',
                          cursor: 'pointer',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={() => handleDownload(doc)}
                      />
                    </div>
                  </div>
                  <Text style={{ fontSize: '12px', color: '#64748b', display: 'block' }}>
                    {[
                      formatBytes(doc?.size),
                      doc?.createdAt ? `Uploaded ${formatDate(doc.createdAt)}` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  {isFinalForm && (
                    <Text
                      style={{
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '4px',
                        fontWeight: 600,
                        color: remainingDays !== null && remainingDays <= 5 ? '#b91c1c' : '#c2410c',
                      }}
                    >
                      {doc?.expiresAt
                        ? `Auto-deletes on ${formatDate(doc.expiresAt)}${
                            remainingDays !== null
                              ? ` (${remainingDays} day${remainingDays === 1 ? '' : 's'} left)`
                              : ''
                          }`
                        : `Auto-deletes ${FINAL_FORM_RETENTION_DAYS} days after upload`}
                    </Text>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DocumitraTeamDocuments;
